import * as React from "react";
import { Divider, Grid, Typography, Box, Link, Dialog, DialogContent, useMediaQuery, Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import NumberFormat from "react-number-format";
import DialogTermsAndConditions from "../../resui/payments/components/DialogTermsAndConditions";
import { TieredPricingTable } from "../../pmui/onboarding/pricingCard";
import { AmountTier, PaidByEnum } from "@inosio/payments-sdk";
import SpinnerModal from "./SpinnerModal";
import { useSelector } from "react-redux";
import { selectPaymentState } from "../../resui/payments/sagas/residentpaymentsSlice";

const useStyles = makeStyles({
  modalContainer: {
    boxShadow: "none",
    borderRadius: "24px",
  },
  paymentSummaryText: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    background: "#F1F0F5",
    borderRadius: "24px",
    marginTop: "13px",
  },
  reviewAndSubmit: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  reviewAndSubmitMainDetails: {
    marginBottom: "18px",
  },
  termsAndConditionLink: {
    fontFamily: "Work Sans",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "22px",
    color: "#39A2DB !important",
    marginTop: "18px",
    cursor: "pointer",
  },
  globalError: {
    display: "flex",
    flexDirection: "row",
    marginBottom: "-20px",
    borderTop: "2px  solid #DB3939",
    borderRadius: "24px",
    padding: "12px 20px 0px",
    width: "100%",
  },
});

interface ReviewAndSubmitPropTypes {
  open: boolean;
  propertyName: string;
  pmCompany?: string;
  name: string;
  paidAmount: number;
  totalAmount: number;
  transactionDate: string;
  paymentFees: number;
  last4: string;
  displayHeader: boolean;
  globalError?: string | null;
  type?: string;
  displayText?: string;
  selectedPaymentMethodTiers?: AmountTier[];
  closeCallback: () => void;
  backCallback: () => void;
  cancelCallback: () => void;
  confirmCallback: () => void;
}

export default function ReviewAndSubmitModal(props: ReviewAndSubmitPropTypes) {
  const classes = useStyles();
  const isOnMobileDevice = useMediaQuery("(max-width:600px)");

  const [openLegaleseDialog, setOpenLegaleseDialog] = React.useState(false);
  const [openFeesDialog, setOpenFeesDialog] = React.useState(false);
  const [showTieredPricingTable, setShowTieredPricingTable] = React.useState(false);

  const handleCloseLegaleseDialog = () => setOpenLegaleseDialog(false);
  const handleCloseFeesDialog = () => setOpenFeesDialog(false);
  const handleLegaleseClick = () => setOpenLegaleseDialog(true);
  const handleFeesClick = () => setOpenFeesDialog(true);

  const { isFetchingPaymentHistory, isFetchingPaymentHistorySuccess, isFetchingPaymentHistoryFailed } = useSelector(selectPaymentState);
  const [disableConfirmBtn, setDisableConfirmBtn] = React.useState(false);

  React.useEffect(() => {
    if (isFetchingPaymentHistory) {
      setIsSpinnerModalOpen(true);
      setDisableConfirmBtn(true);
    }
    if (isFetchingPaymentHistorySuccess) {
      setIsSpinnerModalOpen(false);
      setDisableConfirmBtn(false);
    }
    if (isFetchingPaymentHistoryFailed) {
      setIsSpinnerModalOpen(false);
      setDisableConfirmBtn(true);
    }
  }, [isFetchingPaymentHistory, isFetchingPaymentHistorySuccess, isFetchingPaymentHistoryFailed])

  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);

  return (
    <div id="payment-modal" className={classes.modalContainer}>
      <Dialog
        fullScreen={isOnMobileDevice}
        open={props.open}
        onClose={props.closeCallback}
        fullWidth={true}
        hideBackdrop={true}
        disableEnforceFocus
        PaperProps={{
          style: {
            pointerEvents: "auto",
            height: !isOnMobileDevice ? "700px" : "100%",
            borderRadius: !isOnMobileDevice ? "24px" : undefined,
            boxSizing: "border-box",
          },
        }}
      >
        <DialogContent>
          <React.Fragment>
            {props.displayHeader && (
              <Box className={classes.paymentSummaryText} sx={{ marginBottom: "20px" }}>
                <Typography
                  style={{
                    color: "#14142B",
                    fontSize: "14px",
                    padding: "10px",
                  }}
                  id="paymentSummaryTitleText"
                >
                  {" "}
                  {props?.displayText}
                </Typography>
              </Box>
            )}
            {props.globalError?.length ? (
              <div className={classes.globalError}>
                <Typography
                  style={{
                    fontFamily: "'Open Sans', sans-serif",
                    fontStyle: "normal",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "18px",
                    color: "#DB3939",
                    whiteSpace: 'pre-line',
                    marginBottom: '15px',
                  }}
                >
                  {props.globalError}
                </Typography>
              </div>
            ) : (
              ""
            )}
            <div className={classes.reviewAndSubmit} style={{ marginTop: '20px' }}>
              <Typography id="review-submit">Review And Submit</Typography>
            </div>
            <Box sx={{ width: "100%" }}>
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} position={"relative"}>
                {showTieredPricingTable && (
                  <Box sx={{ width: "230px", height: "max-content", position: "absolute", top: "20%", right: "2%", zIndex: "9", background: "#ffffff", border: "2px solid #eeeeee", borderRadius: "5px" }}>
                    <TieredPricingTable tiers={props.selectedPaymentMethodTiers ?? []} />
                  </Box>
                )}
                <Grid item xs={6}>
                  <div>
                    <Typography
                      style={{
                        color: "#14142B",
                        fontSize: "16px",
                      }}
                      id="review-submit-main-details"
                    >
                      {" "}
                      {props.pmCompany ? `${props.pmCompany.slice(0, 25)}...` : ""}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div>
                    <Typography
                      style={{
                        color: "#14142B",
                        fontSize: "16px",
                      }}
                      id="review-submit-main-details"
                    >
                      {props.propertyName}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className={classes.reviewAndSubmitMainDetails}>
                    <Typography
                      style={{
                        color: "#14142B",
                        fontSize: "16px",
                      }}
                      id="review-submit-main-details"
                    >
                      {props.name}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className={classes.reviewAndSubmitMainDetails}>
                    <Typography
                      style={{
                        color: "#14142B",
                        fontSize: "16px",
                      }}
                      id="review-submit-main-details"
                    >
                      {" "}
                      {/* TODO: Add unit when available */}
                    </Typography>
                  </div>
                </Grid>
              </Grid>
              <Divider />
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ marginTop: "5px" }}>
                <Grid item xs={6}>
                  <div>
                    <Typography id="review-submit-bank-details-text">Payment Method</Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div>
                    <Typography id="review-submit-sub-bank-details">{props.last4}</Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div>
                    <Typography id="review-submit-sub-details">Amount</Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div>
                    <Typography id="review-submit-sub-details">
                      {props.type === "full" ? (
                        " My Full Balance   "
                      ) : (
                        <NumberFormat thousandsGroupStyle="thousand" value={props.paidAmount} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} allowNegative={true} decimalScale={2} fixedDecimalScale={true} />
                      )}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div>
                    <Typography id="review-submit-sub-details">Fee Amount</Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div>
                    <Typography id="review-submit-sub-details">
                      {props.type === "full" && props.selectedPaymentMethodTiers && props.selectedPaymentMethodTiers.some((tier) => tier.PaidBy === PaidByEnum.Payer) ? (
                        <Link
                          className={classes.termsAndConditionLink}
                          onMouseEnter={(event: React.SyntheticEvent) => {
                            setShowTieredPricingTable(true);
                          }}
                          onMouseLeave={(event: React.SyntheticEvent) => {
                            setShowTieredPricingTable(false);
                          }}
                        >
                          Fee
                        </Link>
                      ) : (
                        <NumberFormat thousandsGroupStyle="thousand" value={props.paymentFees} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} allowNegative={true} decimalScale={2} fixedDecimalScale={true} />
                      )}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div>
                    <Typography id="review-submit-sub-details">Total Amount</Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div>
                    <Typography id="review-submit-sub-details">
                      {props.type === "full" ? (
                        <>My Full Balance</>
                      ) : (
                        <NumberFormat thousandsGroupStyle="thousand" value={props.totalAmount} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} allowNegative={true} decimalScale={2} fixedDecimalScale={true} />
                      )}

                      {props.selectedPaymentMethodTiers && props.selectedPaymentMethodTiers.some((tier) => tier.PaidBy === PaidByEnum.Payer) ? (
                        <>
                          {" "}
                          +
                          <Link
                            className={classes.termsAndConditionLink}
                            onMouseEnter={(event: React.SyntheticEvent) => {
                              setShowTieredPricingTable(true);
                            }}
                            onMouseLeave={(event: React.SyntheticEvent) => {
                              setShowTieredPricingTable(false);
                            }}
                          >
                            Fee
                          </Link>
                        </>
                      ) : (
                        <></>
                      )}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div>
                    <Typography id="review-submit-sub-details">Transaction Date</Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div>
                    <Typography id="review-submit-sub-details">{props.transactionDate}</Typography>
                  </div>
                </Grid>
              </Grid>
              <Typography component="h6" variant="subtitle1" id="legalese">
                Legalese
              </Typography>
              <div>
                <Typography variant="body2" gutterBottom id="terms-and-condition">
                  By clicking submit you agree to our{" "}
                  <span
                    className={classes.termsAndConditionLink}
                    onClick={() => {
                      window.open("/terms");
                    }}
                  >
                    {"T&Cs..."}
                  </span>
                </Typography>
                <Typography variant="body2" gutterBottom id="terms-and-condition">
                  Fraudulent Payment will be prosecuted to{" "}
                  <span className={classes.termsAndConditionLink} onClick={handleLegaleseClick}>
                    the fullest extent…
                  </span>
                </Typography>
                <Typography variant="body2" gutterBottom id="terms-and-condition">
                  Payments resulting in returns or chargebacks may incur{" "}
                  <span className={classes.termsAndConditionLink} onClick={handleFeesClick}>
                    additional fees.
                  </span>
                </Typography>
              </div>
              <DialogTermsAndConditions openState={openLegaleseDialog} handleClose={handleCloseLegaleseDialog} default={true} />
              <DialogTermsAndConditions openState={openFeesDialog} handleClose={handleCloseFeesDialog} default={false} />
            </Box>

            <Grid container spacing={2} style={{ marginTop: '0px' }}>
              <Grid item xs={12} sm={4} md={4}>
                <Button color="inherit" onClick={() => props.backCallback()} sx={{ width: "100%", marginLeft: 0 }} id="back-and-cancel-btn">
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Button color="inherit" onClick={() => props.cancelCallback()} sx={{ width: "100%", marginLeft: 0 }} id="back-and-cancel-btn">
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Button
                  sx={{ width: "100%" }}
                  onClick={() => props.confirmCallback()}
                  variant="contained"
                  id="payment-modal-btn"
                  className={"confirm"} //it's for e2e test
                  disabled={disableConfirmBtn}
                >
                  Confirm
                </Button>
              </Grid>
            </Grid>
          </React.Fragment>
        </DialogContent>
      </Dialog>

      {isSpinnerModalOpen && <SpinnerModal isInvisible={true} height={isOnMobileDevice ? '100%' : '700px'} openSpinnerModal={isSpinnerModalOpen} handleCloseSpinnerModal={() => {}} spinnerStatus={''} closeCancel={() => {}} />}
    </div>
  );
}
